import { PlusOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { useMemoizedFn } from 'ahooks';
import { Button, Input, message, Space, Tag } from 'antd';
import { TagProps } from 'antd/es/tag';
import { uniq } from 'lodash-es';
import { useMemo, useState } from 'react';

const ElTag = styled(Tag)`
  padding-top: 5px;
  padding-bottom: 5px;
  font-size: 14px;
`;

export interface InputTagProps {
  readonly?: boolean;
  placeholder?: string;
  tagProps?: TagProps;
  value?: string[];
  tagClosable?: boolean;
  max?: number;
  inputMaxLength?: number;
  autoHidePlaceholder?: boolean;
  onTagClose?: (index: number) => void;
  onChange?: (inputValue: string[]) => void;
}

export const InputTag = (props: InputTagProps) => {
  const {
    readonly,
    placeholder,
    value = [],
    tagClosable = true,
    max,
    inputMaxLength,
    onTagClose,
    onChange,
  } = props;
  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState('');

  const { t } = useTranslate();

  const renderTags = useMemo(() => {
    return value?.map((tag, index) => (
      <ElTag
        key={tag}
        closable={tagClosable && !readonly}
        onClose={() => {
          onTagClose?.(index);
          onChange?.(value.filter((value) => value !== tag));
        }}
      >
        {tag}
      </ElTag>
    ));
  }, [onChange, onTagClose, tagClosable, value, readonly]);

  const handleChange = useMemoizedFn((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  });

  const handlePressEnter = useMemoizedFn(() => {
    if (!inputValue) {
      setInputVisible(false);
      return;
    }
    if (value.includes(inputValue)) {
      message.error(t('errorMsg.标签重复'));
      return;
    }
    const reg = new RegExp(
      "[`~!@#$%^&*()_\\-+=|{}':;',\\[\\].<>/?~！@#￥%……&*（）——+|{}【】‘；：”“’。，、？]",
    );
    if (reg.test(inputValue)) {
      message.error(t('phrase.不可使用特殊符号'));
      return;
    }

    const newValue = [...value, inputValue];
    onChange?.(uniq(newValue));
    setInputValue('');
    setInputVisible(false);
  });

  const handleAddClick = useMemoizedFn(() => {
    setInputVisible(true);
  });

  return (
    <Space wrap>
      {renderTags}
      {inputVisible ? (
        <Input
          disabled={readonly}
          autoFocus
          allowClear
          placeholder={placeholder}
          value={inputValue}
          maxLength={inputMaxLength}
          onBlur={handlePressEnter}
          onPressEnter={handlePressEnter}
          onChange={handleChange}
        />
      ) : (
        <Button
          size="middle"
          type="dashed"
          icon={<PlusOutlined />}
          onClick={handleAddClick}
          disabled={readonly || (!!max && value.length >= max)}
        >
          {t('marketing.新增')}
        </Button>
      )}
    </Space>
  );
};
